import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/common/Footer';

const PrivacyPolicy = () => {
  return (
    <div className='min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-4xl mx-auto'>
        {/* Header */}
        <div className='mb-8'>
          <Link
            to='/home'
            className='inline-flex items-center text-blue-600 hover:text-blue-800 mb-4'
          >
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back to Home
          </Link>
          <h1 className='text-3xl font-bold text-gray-900 mb-2'>
            Privacy Policy
          </h1>
          <p className='text-gray-600'>
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Content */}
        <div className='bg-white rounded-lg shadow-sm p-8 space-y-8'>
          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Introduction
            </h2>
            <p className='text-gray-700 leading-relaxed'>
              Welcome to PCSO E-Lotto. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you
              visit our website and use our services. Please read this privacy
              policy carefully.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Information We Collect
            </h2>
            <div className='space-y-4'>
              <div>
                <h3 className='text-xl font-medium text-gray-800 mb-2'>
                  Personal Information
                </h3>
                <p className='text-gray-700 leading-relaxed'>
                  We may collect personal information that you voluntarily
                  provide when you register for an account, make a purchase, or
                  contact us. This may include your name, email address, phone
                  number, and other contact information.
                </p>
              </div>
              <div>
                <h3 className='text-xl font-medium text-gray-800 mb-2'>
                  Usage Data
                </h3>
                <p className='text-gray-700 leading-relaxed'>
                  We automatically collect certain information when you visit
                  our website, including your IP address, browser type,
                  operating system, referring URLs, and pages viewed.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Cookies and Tracking Technologies
            </h2>
            <div className='space-y-4'>
              <p className='text-gray-700 leading-relaxed'>
                We use cookies and similar tracking technologies to improve your
                experience on our website. Here's what you need to know:
              </p>
              <ul className='list-disc list-inside text-gray-700 space-y-2 ml-4'>
                <li>
                  We use essential cookies to maintain your session and ensure
                  proper functionality of our services.
                </li>
                <li>
                  Analytics cookies help us understand how users interact with
                  our website to improve user experience.
                </li>
                <li>
                  You can manage your cookie preferences through your browser
                  settings.
                </li>
                <li>
                  Some cookies are necessary for the website to function
                  properly and cannot be disabled.
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              How We Use Your Information
            </h2>
            <p className='text-gray-700 leading-relaxed mb-4'>
              We use the information we collect for various purposes, including:
            </p>
            <ul className='list-disc list-inside text-gray-700 space-y-2 ml-4'>
              <li>To provide and maintain our services</li>
              <li>To process transactions and manage your account</li>
              <li>To communicate with you about our services</li>
              <li>To improve our website and services</li>
              <li>To comply with legal obligations</li>
              <li>To ensure security and prevent fraud</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Data Security
            </h2>
            <p className='text-gray-700 leading-relaxed'>
              We implement appropriate technical and organizational security
              measures to protect your personal information against unauthorized
              access, alteration, disclosure, or destruction. However, no method
              of transmission over the Internet or electronic storage is 100%
              secure.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Your Rights
            </h2>
            <p className='text-gray-700 leading-relaxed mb-4'>
              Depending on your location, you may have certain rights regarding
              your personal information:
            </p>
            <ul className='list-disc list-inside text-gray-700 space-y-2 ml-4'>
              <li>The right to access your personal information</li>
              <li>The right to correct inaccurate information</li>
              <li>The right to delete your personal information</li>
              <li>The right to opt-out of certain data processing</li>
              <li>The right to data portability</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Children's Privacy
            </h2>
            <p className='text-gray-700 leading-relaxed'>
              Our services are not intended for children under 18 years of age.
              We do not knowingly collect personal information from children
              under 18. If you are a parent or guardian and believe your child
              has provided us with personal information, please contact us.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Changes to This Privacy Policy
            </h2>
            <p className='text-gray-700 leading-relaxed'>
              We may update our Privacy Policy from time to time. We will notify
              you of any changes by posting the new Privacy Policy on this page
              and updating the "Last updated" date at the top of this policy.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
              Contact Us
            </h2>
            <p className='text-gray-700 leading-relaxed'>
              If you have any questions about this Privacy Policy, please
              contact us at{' '}
              <a
                href='mailto:support@pcsoe-lotto.com'
                className='text-blue-600 hover:text-blue-800 underline'
              >
                support@pcsoe-lotto.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
